import { useEffect, useState } from 'react';

import { BUSINESS_TYPE, FormData, TypeOfDamage } from '@/api/types';
import { HAS_DAMAGE_PHOTOS } from '@/features/v2/vehicle-damage/components/RadioGroupHasDamagePhotos';
import { CAR_CONDITION } from '@/features/v2/vehicle-damage/components/SelectCarCondition';

import constate from 'constate';

export interface AdditionalInfo {
  isVehicleSold: boolean;
  recipientType: BUSINESS_TYPE | null;
  typeOfDamage: TypeOfDamage | null;
  issueType?: number | null;
  summaryModalToggled: boolean;
  postalCode: string | null;
  withDamagePhotosRequired: boolean;
  error: boolean;
  withInvoicesReceiptsOrOtherDocumentsRequired: boolean;
}

function useAdditionalInfo({ data }: { data: FormData }) {
  const [additionalInfo, setAdditionalInfo] = useState<AdditionalInfo>({
    isVehicleSold: false,
    recipientType: null,
    typeOfDamage: null,
    summaryModalToggled: false,
    issueType: undefined,
    postalCode: null,
    withDamagePhotosRequired: false,
    withInvoicesReceiptsOrOtherDocumentsRequired: false,
    error: false,
  });

  useEffect(() => {
    if (data?.payload?.data?.car_condition) {
      setAdditionalInfo((prev) => ({
        ...prev,
        isVehicleSold:
          data?.payload?.data.car_condition === CAR_CONDITION.SOLD_DAMAGED ||
          data?.payload?.data.car_condition === CAR_CONDITION.SOLD_REPAIRED,
        typeOfDamage:
          data?.offer?.type_of_damage ??
          data?.payload?.data?.type_of_damage ??
          null,
        withDamagePhotosRequired:
          data?.payload?.data?.has_damage_photos === HAS_DAMAGE_PHOTOS.YES,
        recipientType: data?.offer?.business_type ?? null,
      }));
    }

    if (data?.product?.additional_info) {
      setAdditionalInfo((prev) => ({
        ...prev,
        issueType: data?.product?.additional_info?.issue_type?.value ?? null,
        postalCode: data?.product?.additional_info?.postal_code ?? null,
      }));
    }
  }, [data]);

  const setSummaryModal = (value: boolean) => {
    setAdditionalInfo((prev) => ({
      ...prev,
      summaryModalToggled: value,
    }));
  };

  const toggleModalSummary = () => {
    setAdditionalInfo((prev) => ({
      ...prev,
      summaryModalToggled: !prev.summaryModalToggled,
    }));
  };

  const setError = (value: boolean) => {
    setAdditionalInfo((prev) => ({
      ...prev,
      error: value,
    }));
  };

  const setWithDamagePhotosRequired = (value: boolean) => {
    setAdditionalInfo((prev) => ({
      ...prev,
      withDamagePhotosRequired: value,
    }));
  };

  const setWithInvoicesReceiptsOrOtherDocumentsRequired = (value: boolean) => {
    setAdditionalInfo((prev) => ({
      ...prev,
      withInvoicesReceiptsOrOtherDocumentsRequired: value,
    }));
  };

  return {
    additionalInfo,
    setAdditionalInfo,
    setSummaryModal,
    toggleModalSummary,
    modalSummary: additionalInfo.summaryModalToggled,
    setError,
    error: additionalInfo.error,
    withDamagePhotosRequired: additionalInfo.withDamagePhotosRequired,
    setWithDamagePhotosRequired,
    withInvoicesReceiptsOrOtherDocumentsRequired:
      additionalInfo.withInvoicesReceiptsOrOtherDocumentsRequired,
    setWithInvoicesReceiptsOrOtherDocumentsRequired,
  };
}

export const [AdditionalInfoProvider, useAdditionalInfoContext] =
  constate(useAdditionalInfo);
